"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  Bookmark,
  BookOpen,
  Check,
  Eye,
  Link2,
  Loader2,
  Mail,
  MapPin,
  Star,
  Users,
} from "lucide-react";

function LinkedInFilledIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M20.452 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.356V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124zM7.119 20.452H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.978 0 1.778-.773 1.778-1.729V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

import { Badge } from "@/components/ui/data-display/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/data-display/hover-card";
import { Checkbox } from "@/components/ui/forms/checkbox";
import { ContactStatusPicker } from "@/components/ContactStatusPicker";
import { EmailCopyWithDropdown } from "@/components/common/EmailCopyWithDropdown";
import { EntityTitle, EntitySubtitle } from "@/components/ui/design-system";
import { DASHBOARD_CHIP } from "@/lib/design-system/typography";
import { cn } from "@/lib/utils";
import { useEventTracking } from "@/hooks/useEventTracking";
import usePaywallGuard from "@/hooks/usePaywallGuard";
import { EventType } from "@/api/types/events";
import type { RelationshipStatus } from "@/types/report-types";

import { EntityActionButton } from "../_internal/EntityActions";
import {
  ContactEntityPresetsAttached,
  attachContactEntityPresets,
} from "./ContactEntity.presets";
import type {
  ContactEntityFields,
  ContactEntityProps,
} from "./ContactEntity.types";

/**
 * Canonical card layout is modeled on the report page's contact render
 * (CompanyReportView.tsx lines 1274-1416). Optional features (selection,
 * favorite, LinkedIn row, extra actions) layer onto that base without
 * changing the core structure.
 */

const DEFAULT_CARD_FIELDS: Required<ContactEntityFields> = {
  email: true,
  phone: false,
  linkedIn: true,
  location: false,
  tags: true,
  relevance: true,
  seniority: true,
  status: true,
};

const DEFAULT_ROW_FIELDS: Required<ContactEntityFields> = {
  email: true,
  phone: false,
  linkedIn: true,
  location: false,
  tags: true,
  relevance: true,
  seniority: true,
  status: true,
};

function resolveFields(
  layout: "card" | "row",
  overrides?: ContactEntityFields,
): Required<ContactEntityFields> {
  const base = layout === "row" ? DEFAULT_ROW_FIELDS : DEFAULT_CARD_FIELDS;
  return { ...base, ...(overrides ?? {}) };
}

function ContactEntityBase(props: ContactEntityProps) {
  const {
    contact,
    layout = "card",
    fields,
    actions = {},
    onSelect,
    onStatusChange,
    onSaveOpportunity,
    onToggleFavorite,
    onAddToLibrary,
    onQuickView,
    onViewProfile,
    onReachOut,
    onOpenReport,
    isSelected = false,
    isFavorite,
    libraryState,
    saveOpportunityState,
    reachOutDisabled = false,
    paywallMode = false,
    statusPickerDefaultOpen = false,
    lookupRoleLabel,
    reportId,
    showHiddenFieldHints = false,
    badge,
    priorityBadge,
    footer,
    className,
  } = props;

  const resolvedFields = resolveFields(layout, fields);
  const favorite = isFavorite ?? contact.isFavorite ?? false;

  const { trackEvent } = useEventTracking();
  const { handleAction: handlePaywallAction } = usePaywallGuard({
    signInFeatureSlug: "contacts-lookup",
    skipPaywall: !paywallMode,
  });

  const triggerPaywall = (element: string) => {
    trackEvent({
      event_type: EventType.CLICK,
      element_id: "contact-paywall-trigger",
      event_details: {
        action: "paywall_trigger",
        element,
        surface: "contact_entity",
        contact_id: contact.id,
        contact_name: contact.fullName,
        company_name: contact.company?.name,
        contact_title: contact.title,
      },
    });
    void handlePaywallAction();
  };

  const trackReachOut = (source: "button" | "email") => {
    trackEvent({
      event_type: EventType.CLICK,
      element_id: "contact-reach-out",
      event_details: {
        action: "reach_out_click",
        funnel: "contact_outreach",
        source,
        surface: layout === "row" ? "contact_entity_row" : "contact_entity",
        contact_id: contact.id,
        contact_name: contact.fullName,
        company_name: contact.company?.name,
        contact_title: contact.title,
      },
    });
  };

  const handleReachOutWithEmail = (selectedEmail: string) => {
    if (!onReachOut) return;
    trackReachOut("email");
    onReachOut(contact, selectedEmail);
  };

  const showSelection = Boolean(actions.selection && onSelect);
  const showStatusPicker = Boolean(
    actions.statusPicker && !actions.saveOpportunity && (paywallMode || (contact.status && onStatusChange)),
  );
  const showSaveOpportunity = Boolean(
    actions.saveOpportunity && (paywallMode || onSaveOpportunity),
  );
  const showFavorite = Boolean(actions.favorite && onToggleFavorite);
  const showLibrary = Boolean(
    actions.library && (paywallMode || (libraryState && onAddToLibrary)),
  );
  const showQuickView = Boolean(
    actions.quickView && (paywallMode || onQuickView),
  );
  const showViewProfile = Boolean(actions.viewProfile && (paywallMode || onViewProfile));
  const showReachOut = Boolean(
    actions.reachOut || (reachOutDisabled && !onReachOut),
  );
  const showViewReport = Boolean(actions.viewReport && reportId);
  const showEmail =
    resolvedFields.email &&
    (Boolean(contact.email) || paywallMode || showHiddenFieldHints);
  const showLinkedIn =
    resolvedFields.linkedIn &&
    (Boolean(contact.linkedInUrl) || paywallMode || showHiddenFieldHints);
  const showLocation = resolvedFields.location && Boolean(contact.location);
  const showLocationPaywall = paywallMode && resolvedFields.location;
  const showLocationHint =
    showHiddenFieldHints && resolvedFields.location && !showLocation;
  const showLocationMeta = showLocation || showLocationPaywall || showLocationHint;
  const titleCompanyLine = [contact.title, contact.company?.name]
    .filter(Boolean)
    .join(", ");

  const hasActionButtons =
    showLibrary ||
    showQuickView ||
    showViewProfile ||
    showViewReport ||
    showReachOut;

  /* ------------------------------ ROW LAYOUT ------------------------------ */
  /* Single-row layout: identity column on the left, right-side stack with
   * priority + relevance % + an optional "Why Contact?" link on top, and
   * the actions cluster (status, Profile, Reach Out) below. Tags and the
   * Why Contact text are hidden from the row itself — they only surface
   * inside the "Why Contact?" hover popover, keeping each contact card
   * visually quiet. */
  if (layout === "row") {
    const showRelevanceChip =
      resolvedFields.relevance && typeof contact.relevanceScore === "number";
    const hasTopRightChips = Boolean(priorityBadge) || showRelevanceChip;

    // Tags are no longer rendered inline — they live inside the
    // "Why Contact?" hover popover alongside the whyContact text (footer).
    // Order: lookup role label first, then contact tags, then seniority.
    type TagChip = {
      key: string;
      label: string;
      variant: "secondary" | "outline";
    };
    const tagChips: TagChip[] = [];
    if (lookupRoleLabel) {
      tagChips.push({
        key: "lookup-role",
        label: lookupRoleLabel,
        variant: "secondary",
      });
    }
    if (resolvedFields.tags && contact.tags?.length) {
      contact.tags.forEach((tag) => {
        tagChips.push({ key: `tag-${tag}`, label: tag, variant: "outline" });
      });
    }
    if (resolvedFields.seniority && contact.seniority) {
      tagChips.push({
        key: "seniority",
        label: contact.seniority,
        variant: "outline",
      });
    }
    // "Why Contact?" popover removed — tags + footer no longer surfaced in the row.
    const hasWhyContactPopover = false;

    const renderActionsCluster = () =>
      showStatusPicker || showSaveOpportunity || hasActionButtons ? (
        <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2 md:ml-0">
          {showSaveOpportunity ? (
            <EntityActionButton
              variant="secondary"
              size="sm"
              icon={saveOpportunityState?.isSaved ? Check : saveOpportunityState?.isSaving ? Loader2 : Bookmark}
              disabled={saveOpportunityState?.isSaved || saveOpportunityState?.isSaving}
              onClick={
                paywallMode
                  ? () => triggerPaywall("save_opportunity")
                  : () => onSaveOpportunity?.(contact)
              }
              className={cn("w-auto", saveOpportunityState?.isSaving && "[&_svg]:animate-spin")}
            >
              {saveOpportunityState?.isSaved ? "Saved" : saveOpportunityState?.isSaving ? "Saving" : "Save"}
            </EntityActionButton>
          ) : showStatusPicker ? (
            <ContactStatusPicker
              contactId={contact.id}
              status={
                paywallMode
                  ? (contact.status ?? "not_contacted")
                  : (contact.status as RelationshipStatus)
              }
              onStatusUpdate={
                paywallMode
                  ? () => triggerPaywall("status_change")
                  : onStatusChange
              }
              defaultOpen={statusPickerDefaultOpen}
              mode="minimal"
            />
          ) : null}

          {hasActionButtons ? (
            <div className="flex items-center gap-1">
              {showViewProfile ? (
                <EntityActionButton
                  variant="secondary"
                  size="sm"
                  icon={Eye}
                  onClick={
                    paywallMode
                      ? () => triggerPaywall("view_profile")
                      : () => onViewProfile?.(contact)
                  }
                  className="w-auto"
                >
                  Profile
                </EntityActionButton>
              ) : null}

              {showQuickView ? (
                <EntityActionButton
                  variant="secondary"
                  size="sm"
                  icon={Eye}
                  onClick={
                    paywallMode
                      ? () => triggerPaywall("quick_view")
                      : () => onQuickView?.(contact)
                  }
                  className="w-auto"
                >
                  Quick view
                </EntityActionButton>
              ) : null}

              {showReachOut ? (
                <EntityActionButton
                  variant="primary"
                  size="sm"
                  icon={Mail}
                  paywall={paywallMode}
                  disabled={
                    paywallMode ? false : reachOutDisabled || !onReachOut
                  }
                  onClick={
                    paywallMode
                      ? () => triggerPaywall("reach_out")
                      : onReachOut
                        ? () => {
                            trackReachOut("button");
                            onReachOut(contact);
                          }
                        : undefined
                  }
                  className="w-auto"
                >
                  Reach out
                </EntityActionButton>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null;

    return (
      <motion.div
        layout
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 6 }}
        exit={{ opacity: 0 }}
        className={cn(
          "flex flex-wrap items-start gap-x-3 gap-y-2 border border-border bg-card px-3 py-2.5 transition-colors hover:bg-background/40 -mt-px first:mt-0",
          className,
        )}
      >

        {/* Identity row: identity column on the left; right-side stack
         * (priority + Why Contact? link on top, actions cluster below)
         * on the right. */}
        <div className="basis-full flex flex-wrap items-start gap-x-3 gap-y-2 md:flex-nowrap md:items-end">
          {showSelection ? (
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onSelect?.(contact)}
              className="mt-0.5 shrink-0 self-start"
            />
          ) : null}

          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {badge ? <span className="shrink-0">{badge}</span> : null}
              <EntityTitle as="span" size="sm" truncate>
                {contact.fullName}
              </EntityTitle>
              {showEmail ? (
                paywallMode || (showHiddenFieldHints && !contact.email) ? (
                  <button
                    type="button"
                    onClick={
                      paywallMode ? () => triggerPaywall("view_email") : undefined
                    }
                    className="flex cursor-pointer items-center gap-1 text-body-sm text-primary underline decoration-dotted hover:text-primary/80"
                  >
                    <Mail className="h-3 w-3" />
                    Reveal email
                  </button>
                ) : (
                  <EmailCopyWithDropdown
                    email={contact.email ?? ""}
                    emails={contact.emails ?? []}
                    textClassName="block truncate text-[13px] leading-[1.35] font-normal text-muted-foreground/85 sm:max-w-[220px]"
                    iconClassName="h-4 w-4"
                    showOnHover={false}
                    onReachOutEmail={
                      onReachOut ? handleReachOutWithEmail : undefined
                    }
                  />
                )
              ) : null}
              {showLinkedIn ? (
                paywallMode || (showHiddenFieldHints && !contact.linkedInUrl) ? (
                  <button
                    type="button"
                    onClick={
                      paywallMode ? () => triggerPaywall("view_linkedin") : undefined
                    }
                    className="shrink-0 inline-flex items-center justify-center p-0.5 rounded text-muted-foreground transition-colors hover:bg-muted hover:text-[#0A66C2] cursor-pointer"
                    title="Unlock LinkedIn profile"
                    aria-label={`Unlock LinkedIn profile for ${contact.fullName}`}
                  >
                    <LinkedInFilledIcon className="h-4 w-4" />
                  </button>
                ) : (
                  <a
                    href={contact.linkedInUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0 inline-flex items-center justify-center p-0.5 rounded text-muted-foreground transition-colors hover:bg-muted hover:text-[#0A66C2] cursor-pointer"
                    title="Open LinkedIn profile"
                    aria-label={`Open LinkedIn profile for ${contact.fullName}`}
                  >
                    <LinkedInFilledIcon className="h-4 w-4" />
                  </a>
                )
              ) : null}
              {showFavorite ? (
                <button
                  type="button"
                  onClick={() => onToggleFavorite?.(contact)}
                  className="shrink-0 text-muted-foreground transition-colors hover:text-yellow-500"
                  aria-label={
                    favorite ? "Unfavorite contact" : "Favorite contact"
                  }
                >
                  <Star
                    className={cn(
                      "h-4 w-4",
                      favorite && "fill-yellow-500 text-yellow-500",
                    )}
                  />
                </button>
              ) : null}
            </div>

            {titleCompanyLine ? (
              <EntitySubtitle as="div" tone="muted" lineClamp={2} className="min-w-0">
                {titleCompanyLine}
              </EntitySubtitle>
            ) : null}

            {showLocationMeta ? (
              showLocationPaywall || showLocationHint ? (
                <button
                  type="button"
                  onClick={
                    showLocationPaywall
                      ? () => triggerPaywall("view_location")
                      : undefined
                  }
                  className="inline-flex min-w-0 max-w-full cursor-pointer items-center gap-1 text-body-sm text-primary underline decoration-dotted hover:text-primary/80"
                >
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="truncate">Reveal location</span>
                </button>
              ) : showLocation ? (
                <span className="inline-flex min-w-0 max-w-full items-center gap-1 text-body-sm text-muted-foreground">
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="truncate">{contact.location}</span>
                </span>
              ) : null
            ) : null}

            {contact.relevanceReason ? (
              <p className="text-caption text-muted-foreground break-words line-clamp-2">
                {contact.relevanceReason}
              </p>
            ) : null}
          </div>

          {/* Right-side stack: chip cluster on top (Why Contact? + priority + relevance %),
           * actions cluster below. */}
          {(hasTopRightChips ||
            hasWhyContactPopover ||
            showStatusPicker ||
            showSaveOpportunity ||
            hasActionButtons) ? (
            <div className="ml-auto flex w-full shrink-0 basis-full flex-col items-end gap-1.5 md:w-auto md:basis-auto md:self-end">
              {(hasTopRightChips || hasWhyContactPopover) ? (
                <div className="inline-flex max-w-full flex-wrap items-center justify-end gap-1.5">
                  {hasWhyContactPopover ? (
                    <HoverCard openDelay={150} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex cursor-pointer items-center text-body-sm text-primary underline decoration-dotted hover:text-primary/80"
                          aria-label="View why this contact matters and tags"
                        >
                          Why Contact?
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent align="end" className="w-72 p-3">
                        <div className="space-y-3">
                          {footer ? <div>{footer}</div> : null}
                          {tagChips.length > 0 ? (
                            <div
                              className={cn(
                                "space-y-1.5",
                                Boolean(footer) && "border-t border-border pt-3",
                              )}
                            >
                              <div className="text-overline text-muted-foreground">
                                Qualities
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {tagChips.map((chip) => (
                                  <Badge
                                    key={chip.key}
                                    variant={chip.variant}
                                    className={cn(
                                      DASHBOARD_CHIP,
                                      "px-1.5 py-0",
                                      chip.variant === "outline" &&
                                        "bg-card text-foreground border-border",
                                    )}
                                  >
                                    {chip.label}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  ) : null}
                  {priorityBadge ? <>{priorityBadge}</> : null}
                  {showRelevanceChip ? (
                    <HoverCard openDelay={150} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex cursor-pointer appearance-none border-0 bg-transparent p-0"
                          aria-label="View relevance score detail"
                        >
                          <Badge
                            variant="secondary"
                            className={cn(
                              DASHBOARD_CHIP,
                              "bg-card text-foreground px-1.5 py-0",
                            )}
                          >
                            {contact.relevanceScore}%
                          </Badge>
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent align="end" className="w-64 p-3">
                        <div className="space-y-2">
                          <div className="text-overline text-muted-foreground">
                            Relevance
                          </div>
                          <div className="text-label text-foreground">
                            {contact.relevanceScore}%
                          </div>
                          {contact.relevanceReason ? (
                            <p className="text-caption text-muted-foreground">
                              {contact.relevanceReason}
                            </p>
                          ) : null}
                          <div className="text-caption text-muted-foreground">
                            How well this contact matches the role
                            you&apos;re targeting, based on title, seniority,
                            and team fit.
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  ) : null}
                </div>
              ) : null}

              {renderActionsCluster()}
            </div>
          ) : null}
        </div>
      </motion.div>
    );
  }

  /* ------------------------------ CARD LAYOUT ----------------------------- */
  /* Structure mirrors CompanyReportView.tsx lines 1274-1416. */
  return (
    <motion.div
      layout
      animate={{ opacity: 1, y: 0 }}
      initial={{ opacity: 0, y: 10 }}
      exit={{ opacity: 0 }}
      className={cn(
        "bg-card border p-2.5",
        contact.spotlight ? "border-amber-500" : "border-border",
        className,
      )}
      style={{ boxShadow: "var(--shadow-warm-sm)" }}
    >
      <div className="flex flex-col gap-2">
        {/* Row 1: name + email on left, status picker on right (same line). */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-start gap-2">
            {showSelection ? (
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onSelect?.(contact)}
                className="mt-0.5 shrink-0"
              />
            ) : null}
            {badge ? <span className="shrink-0">{badge}</span> : null}
            <EntityTitle size="md">{contact.fullName}</EntityTitle>
            {showEmail ? (
              paywallMode ? (
                <button
                  type="button"
                  onClick={() => triggerPaywall("view_email")}
                  className="flex items-center gap-1 text-body-sm text-primary underline decoration-dotted hover:text-primary/80"
                >
                  <Mail className="h-3 w-3" />
                  Reveal email
                </button>
              ) : (
                <EmailCopyWithDropdown
                  email={contact.email ?? ""}
                  emails={contact.emails ?? []}
                  textClassName="block break-all text-[13px] leading-[1.35] font-normal text-muted-foreground/85 sm:max-w-[220px] sm:truncate"
                  iconClassName="h-4 w-4"
                  showOnHover={false}
                  onReachOutEmail={
                    onReachOut ? handleReachOutWithEmail : undefined
                  }
                />
              )
            ) : null}
            {showFavorite ? (
              <button
                type="button"
                onClick={() => onToggleFavorite?.(contact)}
                className="shrink-0 text-muted-foreground transition-colors hover:text-yellow-500"
                aria-label={
                  favorite ? "Unfavorite contact" : "Favorite contact"
                }
              >
                <Star
                  className={cn(
                    "h-4 w-4",
                    favorite && "fill-yellow-500 text-yellow-500",
                  )}
                />
              </button>
            ) : null}
          </div>

          {showSaveOpportunity ? (
            <div className="shrink-0">
              <EntityActionButton
                variant="secondary"
                size="sm"
                icon={saveOpportunityState?.isSaved ? Check : saveOpportunityState?.isSaving ? Loader2 : Bookmark}
                disabled={saveOpportunityState?.isSaved || saveOpportunityState?.isSaving}
                onClick={
                  paywallMode
                    ? () => triggerPaywall("save_opportunity")
                    : () => onSaveOpportunity?.(contact)
                }
                className={cn("w-auto", saveOpportunityState?.isSaving && "[&_svg]:animate-spin")}
              >
                {saveOpportunityState?.isSaved ? "Saved" : saveOpportunityState?.isSaving ? "Saving" : "Save"}
              </EntityActionButton>
            </div>
          ) : showStatusPicker ? (
            <div className="shrink-0">
              <ContactStatusPicker
                contactId={contact.id}
                status={
                  paywallMode
                    ? (contact.status ?? "not_contacted")
                    : (contact.status as RelationshipStatus)
                }
                onStatusUpdate={
                  paywallMode
                    ? () => triggerPaywall("status_change")
                    : onStatusChange
                }
                defaultOpen={statusPickerDefaultOpen}
              />
            </div>
          ) : null}
        </div>

        {/* Row 2: title, company — full width */}
        {titleCompanyLine ? (
          <EntitySubtitle as="div" tone="muted" className="break-words">
            {titleCompanyLine}
          </EntitySubtitle>
        ) : null}

        {/* Optional LinkedIn row — full width */}
        {showLinkedIn ? (
          <div className="flex min-w-0 items-center gap-1 text-body-sm">
            <Link2 className="h-3 w-3 shrink-0 text-muted-foreground" />
            <a
              href={contact.linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer break-all text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {contact.linkedInUrl?.replace(/^https?:\/\//, "")}
            </a>
          </div>
        ) : null}

        {/* Row 3: chip row — full width */}
        {(priorityBadge ||
          resolvedFields.relevance ||
          lookupRoleLabel ||
          (resolvedFields.tags && contact.tags?.length) ||
          (resolvedFields.seniority && contact.seniority)) ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            {priorityBadge ? <>{priorityBadge}</> : null}
            {resolvedFields.relevance &&
            typeof contact.relevanceScore === "number" ? (
              <Badge
                variant="secondary"
                className={cn(
                  DASHBOARD_CHIP,
                  "bg-card text-foreground px-1.5 py-0",
                )}
              >
                {contact.relevanceScore}% relevant
              </Badge>
            ) : null}
            {lookupRoleLabel ? (
              <Badge
                variant="secondary"
                className={cn(DASHBOARD_CHIP, "px-1.5 py-0")}
              >
                {lookupRoleLabel}
              </Badge>
            ) : null}
            {resolvedFields.tags && contact.tags?.length
              ? contact.tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className={cn(
                      DASHBOARD_CHIP,
                      "bg-card text-foreground border-border px-1.5 py-0",
                    )}
                  >
                    {tag}
                  </Badge>
                ))
              : null}
            {resolvedFields.seniority && contact.seniority ? (
              <Badge
                variant="outline"
                className={cn(
                  DASHBOARD_CHIP,
                  "bg-card text-foreground border-border px-1.5 py-0",
                )}
              >
                {contact.seniority}
              </Badge>
            ) : null}
          </div>
        ) : null}

        {/* Optional footer (e.g., "Why Contact") — full width */}
        {footer ? (
          <div className="pt-2 border-t border-border">{footer}</div>
        ) : null}

        {/* Row 4: relevance reason + action buttons on the same line */}
        {(contact.relevanceReason || hasActionButtons) ? (
          <div className="flex items-end justify-between gap-3">
            {contact.relevanceReason ? (
              <p className="min-w-0 flex-1 text-body text-muted-foreground break-words">
                {contact.relevanceReason}
              </p>
            ) : <div className="flex-1" />}

            {hasActionButtons ? (
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {showLibrary && paywallMode ? (
                  <EntityActionButton
                    variant="secondary"
                    size="sm"
                    icon={Users}
                    paywall
                    onClick={() => triggerPaywall("add_to_library")}
                    className="w-auto"
                  >
                    Add
                  </EntityActionButton>
                ) : showLibrary && libraryState && onAddToLibrary ? (
                  <EntityActionButton
                    variant="secondary"
                    size="sm"
                    icon={libraryState.isInLibrary ? Check : Users}
                    disabled={libraryState.isInLibrary || libraryState.isAdding}
                    onClick={() => onAddToLibrary(contact)}
                    className="w-auto"
                  >
                    {libraryState.isInLibrary
                      ? "In Library"
                      : libraryState.isAdding
                        ? "Adding..."
                        : "Add"}
                  </EntityActionButton>
                ) : null}

                {showQuickView ? (
                  <EntityActionButton
                    variant="secondary"
                    size="sm"
                    icon={Eye}
                    onClick={
                      paywallMode
                        ? () => triggerPaywall("quick_view")
                        : () => onQuickView?.(contact)
                    }
                    className="w-auto"
                  >
                    Quick View
                  </EntityActionButton>
                ) : null}

                {showViewProfile ? (
                  <EntityActionButton
                    variant="secondary"
                    size="sm"
                    icon={Eye}
                    onClick={
                      paywallMode
                        ? () => triggerPaywall("view_profile")
                        : () => onViewProfile?.(contact)
                    }
                    className="w-auto"
                  >
                    Profile
                  </EntityActionButton>
                ) : null}

                {showViewReport && reportId ? (
                  <Link
                    href={`/d/reports?reportId=${reportId}`}
                    className="contents"
                    onClick={() => onOpenReport?.(contact)}
                  >
                    <EntityActionButton
                      variant="secondary"
                      size="sm"
                      icon={BookOpen}
                      className="w-auto"
                    >
                      Report
                    </EntityActionButton>
                  </Link>
                ) : null}

                {showReachOut ? (
                  <EntityActionButton
                    variant="primary"
                    size="sm"
                    icon={Mail}
                    paywall={paywallMode}
                    disabled={
                      paywallMode ? false : reachOutDisabled || !onReachOut
                    }
                    onClick={
                      paywallMode
                        ? () => triggerPaywall("reach_out")
                        : onReachOut
                          ? () => {
                              trackReachOut("button");
                              onReachOut(contact);
                            }
                          : undefined
                    }
                    className="w-auto"
                  >
                    Reach Out
                  </EntityActionButton>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}

type ContactEntityComponent = typeof ContactEntityBase &
  ContactEntityPresetsAttached;

export const ContactEntity = attachContactEntityPresets(
  ContactEntityBase,
) as ContactEntityComponent;
